import { FaCoins, FaDonate, FaMosque, FaUsers } from 'react-icons/fa'
import Carousel from '../components/Carousel'
import { Button } from '../../shared/components/Button'
import { useEffect, useState } from 'react'
import AnimatedSection from '../components/AnimatedSection'
import CarouselCards from '../components/CarouselCards'
import QuranQuote from '../components/QuranQuote'
import { useNavigate } from 'react-router-dom'
import { languageUtil } from '../../util/languageUtils'
import { fetchWithLanguage } from '../../util/fetchFunction'
import { useTranslation } from 'react-i18next'
import QuickDonationCard from '../components/QuickDonationCard'
import { useCart } from '../../util/CartContext'
import { ErrorOverlay, LoadingOverlay } from '../../shared/components/Loading'
import SuccessModal from '../components/SuccessModal'
import CartModal from '../components/CartModal'

export default function Home() {
    const [hero, setHero] = useState([])
    const [campaigns, setCampaigns] = useState([])
    const [cards, setCards] = useState([])
    const [isCartModalOpen, setIsCartModalOpen] = useState(false)
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(true)
    const navigate = useNavigate()

    const { t } = useTranslation()
    const { language } = languageUtil()

    useEffect(() => {
        const fetchHeroBanners = async () => {
            await fetchWithLanguage(`get-hero-banners`, setHero, setError, setLoading)
        }

        const fetchCampaigns = async () => {
            await fetchWithLanguage('current-projects', setCampaigns, setError, setLoading, {
                transform: campaign =>
                    campaign.map(item => ({
                        imageSrc: item.image_preview || item.header_image,
                        label: item.title || item.title_en,
                        description: item.small_description || item.small_description_en,
                        buttonLabel:
                            item.call_to_action || item.call_to_action_en || 'More Information',
                        onButtonClick: () => navigate(`/projects/${item.id}`),
                    })),
            })
        }

        const fetchReligiousDues = async () => {
            await fetchWithLanguage('current-projects', setCards, setError, setLoading, {
                transform: dues =>
                    dues.map(item => ({
                        imageSrc: item.image_preview || item.header_image,
                        label: item.title || item.title_en,
                        description: item.small_description || item.small_description_en,
                        buttonLabel:
                            item.call_to_action || item.call_to_action_en || 'More Information',
                        onButtonClick: () => navigate(`/religious-dues/${item.id}`),
                    })),
            })
        }
        fetchReligiousDues()
        fetchHeroBanners()
        fetchCampaigns()
        // eslint-disable-next-line
    }, [language])

    const slides = hero.map((item, index) => (
        <div key={index} className='relative w-full h-full'>
            <img
                src={item.image_preview || item.header_image}
                alt={item.title}
                className='absolute inset-0 object-cover w-full h-full'
            />

            <div className='relative z-10 flex items-center justify-center w-full h-full px-4 text-center text-white bg-black/40 md:px-24'>
                <div className='w-full max-w-4xl'>
                    <h1 className='mb-4 text-3xl font-bold md:text-5xl'>
                        {item.title || item.title_en}
                    </h1>
                    <p className='mb-6 text-lg md:text-xl'>{item.subtitle || item.subtitle_en}</p>
                    <button
                        className='px-6 py-3 font-semibold text-black transition bg-white rounded-full hover:bg-gray-200'
                        onClick={() => navigate('/projects')}
                    >
                        {item.call_to_action || item.call_to_action_en}
                    </button>
                </div>
            </div>
        </div>
    ))

    const { addDonationItem } = useCart()

    const handleDonationChange = donationPayload => {
        addDonationItem({
            data: {
                id: donationPayload.donation_option_id,
                amount: donationPayload.amount,
                frequent_option: donationPayload.frequent_option,
                donation_type: donationPayload.donation_type,
            },
            id: donationPayload.parent_id,
            type: donationPayload.project?.type || 'project',
            onSuccess: () => setIsSuccessModalOpen(true),
            onError: err => console.error('Error:', err),
        })
    }

    return (
        <div>
            {loading ? (
                <LoadingOverlay message={t('loading')} />
            ) : (
                <>
                    {error && <ErrorOverlay message={error} onClose={() => setError(null)} />}

                    <SuccessModal
                        isOpen={isSuccessModalOpen}
                        onClose={() => setIsSuccessModalOpen(false)}
                        onOpenCart={() => setIsCartModalOpen(true)}
                    />
                    <CartModal isOpen={isCartModalOpen} onClose={() => setIsCartModalOpen(false)} />
                    <Carousel
                        items={slides}
                        autoPlay
                        interval={4000}
                        size='large'
                        loading={loading}
                    />
                    <div className='mx-auto my-8 '>
                        <AnimatedSection>
                            <section className='flex flex-wrap items-center justify-center gap-3 px-4 py-8 mx-auto space-y-6 max-w-7xl'>
                                <QuickDonationCard onDonate={handleDonationChange} />
                            </section>
                        </AnimatedSection>
                        <hr className='h-0.5 my-16 bg-gray-300 ' />
                        {/* News */}
                        <AnimatedSection>
                            <section className='max-w-5xl px-4 mx-auto sm:px-6 md:px-8'>
                                <div className='flex flex-col items-start justify-between gap-4 mb-6 sm:flex-row sm:items-center'>
                                    <div>
                                        <h1 className='text-xl sm:text-2xl highlighter'>
                                            {t('homepage.projects.title')}
                                        </h1>
                                        <br />
                                        <label className='text-sm text-gray-600'>
                                            {t('homepage.projects.subtitle')}
                                        </label>
                                    </div>
                                    <Button
                                        variant='secondary'
                                        onClick={() => navigate('/projects')}
                                    >
                                        {t('homepage.projects.button')}
                                    </Button>
                                </div>
                                <CarouselCards
                                    items={campaigns}
                                    cardsPerPage={
                                        window.innerWidth < 640
                                            ? 1
                                            : window.innerWidth < 768
                                              ? 2
                                              : 3
                                    }
                                />
                            </section>
                        </AnimatedSection>
                        <hr className='h-0.5 my-16 bg-gray-300' />
                        {/* About Us */}
                        <AnimatedSection>
                            <section className='max-w-5xl px-4 mx-auto' id='about'>
                                <div className='flex flex-col items-center mb-8 text-center'>
                                    <h2 className='mb-8 text-2xl font-bold text-center text-gray-800 highlighter '>
                                        {t('homepage.about.title')}
                                    </h2>
                                </div>
                                <div className='flex flex-col items-center gap-10 md:flex-row'>
                                    {/* Image Section */}
                                    <div className='flex flex-col w-full gap-4 md:w-1/2'>
                                        <div className='flex gap-4 h-36'>
                                            <img
                                                src='https://images.unsplash.com/photo-1593113598332-cd288d649433?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'
                                                alt='Top Left'
                                                className='object-cover w-1/2 h-auto rounded-lg'
                                            />
                                            <img
                                                src='https://images.unsplash.com/photo-1732919258502-023e94fc1109?q=80&w=688&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'
                                                alt='Top Right'
                                                className='object-cover w-1/2 h-auto rounded-lg'
                                            />
                                        </div>
                                        <img
                                            src='https://images.unsplash.com/photo-1735504647714-575fdb0a8a47?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'
                                            alt='Bottom Full'
                                            className='object-cover w-full rounded-lg h-36'
                                        />
                                    </div>

                                    {/* Text Section */}
                                    <div className='w-full md:w-1/2'>
                                        <p className='leading-relaxed text-gray-700 text-md'>
                                            {t('homepage.about.description')}
                                        </p>
                                    </div>
                                </div>
                            </section>
                        </AnimatedSection>
                        {/* Our Mission */}
                        <section className='w-screen px-4 py-16 my-16 custom-pattern'>
                            <div className='max-w-5xl mx-auto'>
                                {/* Center container for first two */}
                                <div className='flex flex-col items-center text-center'>
                                    <h2 className='mb-4 text-2xl font-semibold highlighter'>
                                        {t('homepage.mission.title')}
                                    </h2>
                                    <p className='max-w-3xl text-lg leading-relaxed highlighter'>
                                        {t('homepage.mission.content')}
                                    </p>
                                </div>

                                {/* Right-aligned since date */}
                                <div className='flex justify-end mt-8'>
                                    <p className='text-sm highlighter'>
                                        {t('homepage.mission.since')}
                                    </p>
                                </div>
                            </div>
                        </section>
                        {/* Religious Dues */}
                        <AnimatedSection>
                            <section className='max-w-5xl mx-auto'>
                                <QuranQuote
                                    verse='وَأَقِيمُوا۟ ٱلصَّلَوٰةَ وَءَاتُوا۟ ٱلزَّكَوٰةَ ۚ وَمَا تُقَدِّمُوا۟ لِأَنفُسِكُم مِّنْ خَيْرٍۢ تَجِدُوهُ عِندَ ٱللَّهِ ۗ إِنَّ ٱللَّهَ بِمَا تَعْمَلُونَ بَصِيرٌۭ'
                                    translation='Establish prayer, and pay alms-tax. Whatever good you send forth for yourselves, you will ˹certainly˺ find ˹its reward˺ with Allah. Surely Allah is All-Seeing of what you do.'
                                    source="Qur'an 2:110"
                                />

                                <div className='max-w-5xl px-4 py-10 mx-auto my-16 bg-white border border-yellow-100 shadow-lg rounded-xl'>
                                    {/* Elegant header */}
                                    <div className='mb-8 text-center'>
                                        <div className='inline-flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-gradient-to-br from-yellow-100 to-amber-100'>
                                            <FaMosque className='text-xl text-amber-600' />
                                        </div>
                                        <h1 className='text-2xl font-bold text-gray-900'>
                                            {t('homepage.religiousDues.title')}
                                        </h1>
                                        <div className='w-16 h-1 mx-auto mt-2 rounded-full bg-gradient-to-r from-yellow-300 to-amber-300'></div>
                                    </div>

                                    {/* Clean icon labels */}
                                    <div className='flex flex-wrap justify-center gap-6 mb-10'>
                                        {[
                                            {
                                                icon: FaMosque,
                                                label: t('homepage.religiousDues.labels.sharia'),
                                                color: 'amber',
                                            },
                                            {
                                                icon: FaCoins,
                                                label: t(
                                                    'homepage.religiousDues.labels.taxDeductible'
                                                ),
                                                color: 'emerald',
                                            },
                                            {
                                                icon: FaDonate,
                                                label: t(
                                                    'homepage.religiousDues.labels.distribution'
                                                ),
                                                color: 'blue',
                                            },
                                        ].map((item, index) => (
                                            <div
                                                key={index}
                                                className='flex items-center gap-3 px-4 py-2 rounded-lg bg-gray-50'
                                            >
                                                <div
                                                    className={`p-2 bg-${item.color}-100 rounded-md`}
                                                >
                                                    <item.icon
                                                        className={`text-lg text-${item.color}-600`}
                                                    />
                                                </div>
                                                <span className='text-sm font-medium text-gray-700'>
                                                    {item.label}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Simple cards */}
                                    <div className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3'>
                                        {cards.map((card, index) => (
                                            <div
                                                key={index}
                                                className='p-6 transition-all duration-300 border border-gray-200 rounded-lg bg-gradient-to-b from-white to-yellow-50 hover:border-yellow-300 hover:shadow-md'
                                            >
                                                <div className='flex items-start gap-4 mb-4'>
                                                    {card.imageSrc ? (
                                                        <img
                                                            src={card.imageSrc}
                                                            alt={card.label}
                                                            className='object-cover w-16 h-16 rounded-lg'
                                                        />
                                                    ) : (
                                                        <div className='flex items-center justify-center w-16 h-16 rounded-lg bg-gradient-to-br from-yellow-100 to-amber-100'>
                                                            <FaDonate className='text-xl text-amber-500' />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <h3 className='font-bold text-gray-900 line-clamp-2'>
                                                            {card.label}
                                                        </h3>
                                                        <p className='mt-1 text-sm text-gray-600 line-clamp-2'>
                                                            {card.description}
                                                        </p>
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={() => navigate('/religious-dues')}
                                                    className='w-full py-2.5 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-medium rounded-lg transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]'
                                                >
                                                    {card.buttonLabel}
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </section>
                        </AnimatedSection>

                        <hr className='h-0.5 my-16 bg-gray-300' />
                        {/* Our Impact */}
                        <AnimatedSection>
                            <section className='grid max-w-5xl grid-cols-1 gap-6 px-4 mx-auto md:grid-cols-2'>
                                {/* Sponsors Section - Full Width */}
                                {/* <div className='p-6 border border-gray-100 shadow-sm bg-gradient-to-br from-white to-gray-50 rounded-xl md:col-span-2'>
                                    <div className='mb-6 text-center'>
                                        <h3 className='mb-2 text-lg font-semibold text-gray-900 highlighter'>
                                            {t('homepage.impact.sponsorsTitle') ||
                                                'Our Valued Sponsors'}
                                        </h3>

                                        <p className='text-sm text-gray-600'>
                                            {t('homepage.impact.sponsorsSubtitle') ||
                                                'Supporting our mission to make a difference'}
                                        </p>
                                    </div>

                                    <div className='grid grid-cols-2 gap-6 md:grid-cols-5'>
                                        <div className='flex items-center justify-center p-2 transition-shadow bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md'>
                                            <img
                                                src='/img/sponsors/1.png'
                                                alt='Masjid Ghosia'
                                                className='object-contain w-auto max-h-14'
                                            />
                                        </div>

                                        <div className='flex items-center justify-center p-4 transition-shadow bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md'>
                                            <img
                                                src='/img/sponsors/2.png'
                                                alt='The Qamar Trust'
                                                className='object-contain w-auto max-h-12'
                                            />
                                        </div>

                                        <div className='flex items-center justify-center p-4 transition-shadow bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md'>
                                            <img
                                                src='/img/sponsors/3.png'
                                                alt='Abu Faisal Trust'
                                                className='object-contain w-auto max-h-12'
                                            />
                                        </div>

                                        <div className='flex items-center justify-center p-2 transition-shadow bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md'>
                                            <img
                                                src='/img/sponsors/4.png'
                                                alt='Wilthy Trees'
                                                className='object-contain w-auto max-h-14'
                                            />
                                        </div>

                                        <div className='flex items-center justify-center p-4 transition-shadow bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md'>
                                            <img
                                                src='/img/sponsors/5.png'
                                                alt='Quwat Foundation'
                                                className='object-contain w-auto max-h-12'
                                            />
                                        </div>
                                    </div>
                                </div> */}

                                {/* Bottom Full Width Card - Keep as is */}
                                <div className='p-6 border border-gray-100 shadow-sm bg-gradient-to-br from-white to-amber-50 rounded-xl md:col-span-2'>
                                    <div className='flex items-start justify-between'>
                                        <div className='flex-1'>
                                            <div className='flex items-center gap-3 mb-4'>
                                                <div className='p-2 rounded-lg bg-gradient-to-br from-amber-100 to-yellow-100'>
                                                    <FaUsers className='text-xl text-amber-600' />
                                                </div>
                                                <h3 className='text-lg font-semibold text-gray-900'>
                                                    {t('homepage.impact.volunteersTitle')}
                                                </h3>
                                            </div>
                                            <p className='text-gray-600'>
                                                {t('homepage.impact.volunteersText')}
                                            </p>
                                        </div>
                                    </div>

                                    <div className='pt-6 mt-6 border-t border-gray-100'>
                                        <button
                                            onClick={() => navigate('/get-involved')}
                                            className='flex items-center gap-2 px-6 py-3 ml-auto font-medium transition-all duration-300 border rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 hover:from-amber-100 hover:to-yellow-100 text-amber-700 border-amber-200 hover:border-amber-300'
                                        >
                                            <span>{t('homepage.impact.joinButton')}</span>
                                            <svg
                                                className='w-4 h-4'
                                                fill='none'
                                                stroke='currentColor'
                                                viewBox='0 0 24 24'
                                            >
                                                <path
                                                    strokeLinecap='round'
                                                    strokeLinejoin='round'
                                                    strokeWidth='2'
                                                    d='M14 5l7 7m0 0l-7 7m7-7H3'
                                                ></path>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </section>
                        </AnimatedSection>
                    </div>
                </>
            )}
        </div>
    )
}
